(function () {
  const DRAG_THRESHOLD = 12;
  const TOOLBAR_SPACE = 50;
  const MODAL_MAX_WIDTH = 440;
  const WIDE_LAYOUT = 1100;

  if (window.__productFloatingVideoBooted) return;
  window.__productFloatingVideoBooted = true;

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    if (isDesignMode()) return;
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      /* Private browsing can block storage. The widget still works. */
    }
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || root.dataset.pfvReady === 'true') return;
    if (root.hasAttribute('hidden') && !isDesignMode()) return;

    root.dataset.pfvReady = 'true';

    const player = root.querySelector('[data-pfv-player]');
    const frame = root.querySelector('[data-pfv-frame]');
    const video = root.querySelector('[data-pfv-video]');
    const openButton = root.querySelector('[data-pfv-open]');
    const dismissButton = root.querySelector('[data-pfv-dismiss]');
    const minimizeButton = root.querySelector('[data-pfv-minimize]');
    const closeButton = root.querySelector('[data-pfv-close]');
    const backdrop = root.querySelector('[data-pfv-backdrop]');

    if (!(player instanceof HTMLElement) || !(frame instanceof HTMLElement)) return;

    const controller = new AbortController();
    const { signal } = controller;
    root._pfvAbort = controller;

    const state = {
      tracking: false,
      moved: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      originLeft: 0,
      originTop: 0,
      userPlaced: player.style.left !== '' && player.style.top !== '',
      anchor: null,
      resetTimer: 0,
    };

    if (video instanceof HTMLVideoElement) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.play().catch(function () {});
    }

    function viewportBox() {
      const visual = window.visualViewport;
      if (!visual) {
        return {
          left: 0,
          top: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }

      return {
        left: visual.offsetLeft,
        top: visual.offsetTop,
        width: visual.width,
        height: visual.height,
      };
    }

    function insets() {
      const edge = Number(root.dataset.edge) || 16;
      const clearance = Number(root.dataset.clearance) || 104;
      const styles = getComputedStyle(root);
      const safeTop = parseFloat(styles.getPropertyValue('--pfv-safe-top')) || 0;
      const safeRight = parseFloat(styles.getPropertyValue('--pfv-safe-right')) || 0;
      const safeBottom = parseFloat(styles.getPropertyValue('--pfv-safe-bottom')) || 0;
      const safeLeft = parseFloat(styles.getPropertyValue('--pfv-safe-left')) || 0;
      const bodyStyles = getComputedStyle(document.body);
      const headerHeight =
        parseFloat(bodyStyles.getPropertyValue('--header-group-height')) ||
        parseFloat(bodyStyles.getPropertyValue('--header-height')) ||
        0;
      const keepAboveStickyBar = window.innerWidth < WIDE_LAYOUT;

      return {
        top: headerHeight + edge + safeTop,
        right: edge + safeRight,
        bottom: (keepAboveStickyBar ? clearance : edge) + safeBottom,
        left: edge + safeLeft,
      };
    }

    function clamp(left, top, width, height) {
      const view = viewportBox();
      const bounds = insets();
      const minLeft = view.left + bounds.left;
      const minTop = view.top + bounds.top;
      const maxLeft = view.left + view.width - width - bounds.right;
      const maxTop = view.top + view.height - height - bounds.bottom;

      return {
        left: Math.min(Math.max(minLeft, left), Math.max(minLeft, maxLeft)),
        top: Math.min(Math.max(minTop, top), Math.max(minTop, maxTop)),
      };
    }

    function pinToViewport() {
      const rect = player.getBoundingClientRect();
      player.style.right = 'auto';
      player.style.bottom = 'auto';
      player.style.left = rect.left + 'px';
      player.style.top = rect.top + 'px';
      return rect;
    }

    function closedWidth() {
      const styles = getComputedStyle(root);
      const mobile = window.innerWidth < 750;
      const raw = mobile
        ? styles.getPropertyValue('--pfv-width-mobile')
        : styles.getPropertyValue('--pfv-width-desktop');
      return parseFloat(raw) || (mobile ? 76 : 120);
    }

    function applyPosition(left, top, width, height) {
      const rect = player.getBoundingClientRect();
      const next = clamp(left, top, width || rect.width, height || rect.height);
      player.style.right = 'auto';
      player.style.bottom = 'auto';
      player.style.left = next.left + 'px';
      player.style.top = next.top + 'px';
      return next;
    }

    function savePosition(left, top) {
      state.userPlaced = true;
      storageSet(root.dataset.positionKey, JSON.stringify({ left: left, top: top }));
    }

    function modalSize() {
      const view = viewportBox();
      const gutter = 16;
      const maxWidth = Math.min(MODAL_MAX_WIDTH, view.width - gutter * 2);
      const maxFrameHeight = view.height - gutter * 2 - TOOLBAR_SPACE;
      const width = Math.max(160, Math.min(maxWidth, (maxFrameHeight * 9) / 16));
      const height = width * (16 / 9) + TOOLBAR_SPACE;

      return {
        width: width,
        left: view.left + Math.max(gutter, (view.width - width) / 2),
        top: view.top + Math.max(gutter, (view.height - height) / 2),
      };
    }

    function setDialog(open) {
      if (open) {
        player.setAttribute('role', 'dialog');
        player.setAttribute('aria-modal', 'true');
        return;
      }

      player.removeAttribute('role');
      player.removeAttribute('aria-modal');
    }

    function lockScroll(locked) {
      document.documentElement.classList.toggle('product-floating-video-scroll-lock', locked);
    }

    function openModal() {
      if (root.classList.contains('is-open') || !(video instanceof HTMLVideoElement)) return;

      const start = pinToViewport();
      state.anchor = { left: start.left, top: start.top };
      player.style.width = start.width + 'px';
      player.getBoundingClientRect();

      root.classList.add('is-open');
      player.classList.add('is-open');
      if (backdrop instanceof HTMLElement) backdrop.hidden = false;

      const size = modalSize();
      player.style.width = size.width + 'px';
      player.style.left = size.left + 'px';
      player.style.top = size.top + 'px';

      video.controls = true;
      video.muted = false;
      video.removeAttribute('muted');
      try {
        video.volume = 1;
      } catch (error) {
        /* Some mobile browsers ignore scripted volume and use the hardware buttons. */
      }
      video.play().catch(function () {});

      setDialog(true);
      lockScroll(true);
      if (openButton instanceof HTMLElement) openButton.setAttribute('aria-expanded', 'true');
      if (minimizeButton instanceof HTMLButtonElement) minimizeButton.focus({ preventScroll: true });
    }

    function minimize() {
      if (!root.classList.contains('is-open')) return;

      const wasPaused = video instanceof HTMLVideoElement ? video.paused : true;
      const width = closedWidth();
      const height = width * (16 / 9);
      const destination = state.anchor
        ? clamp(state.anchor.left, state.anchor.top, width, height)
        : null;

      root.classList.remove('is-open');
      player.classList.remove('is-open');
      player.style.width = '';
      if (backdrop instanceof HTMLElement) backdrop.hidden = true;
      setDialog(false);
      lockScroll(false);

      if (video instanceof HTMLVideoElement) {
        video.controls = false;
        video.removeAttribute('controls');
        video.muted = true;
        video.setAttribute('muted', '');
        if (!wasPaused) video.play().catch(function () {});
      }

      if (destination) {
        player.style.right = 'auto';
        player.style.bottom = 'auto';
        player.style.left = destination.left + 'px';
        player.style.top = destination.top + 'px';
      }

      window.clearTimeout(state.resetTimer);
      if (!state.userPlaced) {
        state.resetTimer = window.setTimeout(function () {
          if (root.classList.contains('is-open') || state.userPlaced) return;
          player.style.left = '';
          player.style.top = '';
          player.style.right = '';
          player.style.bottom = '';
        }, 280);
      }

      if (openButton instanceof HTMLElement) {
        openButton.setAttribute('aria-expanded', 'false');
        openButton.focus({ preventScroll: true });
      }
    }

    function dismiss() {
      if (root.classList.contains('is-open')) {
        root.classList.remove('is-open');
        player.classList.remove('is-open');
        if (backdrop instanceof HTMLElement) backdrop.hidden = true;
        setDialog(false);
        lockScroll(false);
      }

      if (video instanceof HTMLVideoElement) {
        video.pause();
        video.muted = true;
      }

      root.setAttribute('hidden', '');
    }

    function onPointerDown(event) {
      if (root.classList.contains('is-open') || event.button > 0) return;
      if (event.target instanceof Element && event.target.closest('[data-pfv-dismiss], [data-pfv-toolbar]')) return;

      window.clearTimeout(state.resetTimer);
      const rect = player.getBoundingClientRect();
      state.tracking = true;
      state.moved = false;
      state.pointerId = event.pointerId;
      state.startX = event.clientX;
      state.startY = event.clientY;
      state.originLeft = rect.left;
      state.originTop = rect.top;
    }

    function onPointerMove(event) {
      if (!state.tracking || event.pointerId !== state.pointerId) return;

      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;
      if (!state.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;

      if (!state.moved) {
        state.moved = true;
        player.classList.add('is-dragging');
        player.style.right = 'auto';
        player.style.bottom = 'auto';
        player.style.left = state.originLeft + 'px';
        player.style.top = state.originTop + 'px';
        try {
          player.setPointerCapture(event.pointerId);
        } catch (error) {
          /* Pointer capture is unavailable for some synthetic events. Drag still tracks the pointer. */
        }
      }

      applyPosition(state.originLeft + deltaX, state.originTop + deltaY);
    }

    function onPointerUp(event) {
      if (!state.tracking || event.pointerId !== state.pointerId) return;

      state.tracking = false;
      player.classList.remove('is-dragging');

      try {
        if (player.hasPointerCapture(event.pointerId)) {
          player.releasePointerCapture(event.pointerId);
        }
      } catch (error) {
        /* Ignore when this pointer was never captured. */
      }

      if (!state.moved) return;

      const rect = player.getBoundingClientRect();
      savePosition(rect.left, rect.top);
      window.setTimeout(function () {
        state.moved = false;
      }, 0);
    }

    player.addEventListener('pointerdown', onPointerDown, { signal: signal });
    player.addEventListener('pointermove', onPointerMove, { signal: signal });
    player.addEventListener('pointerup', onPointerUp, { signal: signal });
    player.addEventListener('pointercancel', onPointerUp, { signal: signal });
    player.addEventListener('dragstart', function (event) {
      event.preventDefault();
    }, { signal: signal });

    if (openButton instanceof HTMLElement) {
      openButton.addEventListener('click', function () {
        if (state.moved) return;
        openModal();
      }, { signal: signal });
    }

    if (dismissButton instanceof HTMLButtonElement) {
      dismissButton.addEventListener('click', function (event) {
        event.stopPropagation();
        dismiss();
      }, { signal: signal });
    }

    if (minimizeButton instanceof HTMLButtonElement) {
      minimizeButton.addEventListener('click', minimize, { signal: signal });
    }

    if (closeButton instanceof HTMLButtonElement) {
      closeButton.addEventListener('click', dismiss, { signal: signal });
    }

    if (backdrop instanceof HTMLElement) {
      backdrop.addEventListener('click', minimize, { signal: signal });
    }

    document.addEventListener('keydown', function (event) {
      if (!root.classList.contains('is-open') || event.key !== 'Escape') return;
      event.preventDefault();
      minimize();
    }, { signal: signal });

    function onViewportChange() {
      if (root.classList.contains('is-open')) {
        player.classList.add('is-resizing');
        const size = modalSize();
        player.style.width = size.width + 'px';
        player.style.left = size.left + 'px';
        player.style.top = size.top + 'px';
        window.requestAnimationFrame(function () {
          player.classList.remove('is-resizing');
        });
        return;
      }

      if (!state.userPlaced) return;

      const rect = player.getBoundingClientRect();
      applyPosition(rect.left, rect.top);
    }

    window.addEventListener('resize', onViewportChange, { signal: signal });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportChange, { signal: signal });
    }

    if (state.userPlaced) {
      const rect = player.getBoundingClientRect();
      applyPosition(rect.left, rect.top);
    }

    window.requestAnimationFrame(function () {
      player.classList.add('is-ready');
    });

    root.addEventListener('pfv:destroy', function () {
      if (video instanceof HTMLVideoElement) video.pause();
      lockScroll(false);
      controller.abort();
    }, { signal: signal });
  }

  function boot(scope) {
    const context = scope && scope.querySelectorAll ? scope : document;
    context.querySelectorAll('[data-product-floating-video]').forEach(init);
  }

  function restore(root) {
    if (!(root instanceof HTMLElement) || isDesignMode()) return;

    const raw = storageGet(root.dataset.positionKey);
    if (!raw) return;

    try {
      const position = JSON.parse(raw);
      const player = root.querySelector('[data-pfv-player]');
      if (!(player instanceof HTMLElement)) return;
      if (!Number.isFinite(position.left) || !Number.isFinite(position.top)) return;
      player.style.left = position.left + 'px';
      player.style.top = position.top + 'px';
      player.style.right = 'auto';
      player.style.bottom = 'auto';
    } catch (error) {
      /* Ignore unreadable saved positions and use the theme setting. */
    }
  }

  try {
    const staleHiddenKeys = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && key.indexOf('product-floating-video-hidden-') === 0) staleHiddenKeys.push(key);
    }
    staleHiddenKeys.forEach(function (key) {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    /* Storage can be blocked. A refresh still shows the video because hiding is not saved. */
  }

  document.querySelectorAll('[data-product-floating-video]').forEach(restore);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector('[data-product-floating-video]');
    if (!root) return;
    restore(root);
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target.querySelector('[data-product-floating-video]');
    root?.dispatchEvent(new CustomEvent('pfv:destroy'));
  });
})();
